/**
 * Claro, escuro, o do sistema — ou Ligado.
 *
 * Claro é o primário e "sistema" é o padrão: a cena de uso é uma secretaria
 * de escola à tarde, e o artefato final sai impresso. Escuro existe porque a
 * virada de ano letivo invade a noite.
 *
 * **Ligado** é o design system do Ligado (`styles/ligado.css`): preto,
 * lima, Plus Jakarta Sans, pílulas e a moldura preta. O botão dele é o LED
 * lima da marca, e não um ícone: é o símbolo que o DS de lá manda usar.
 *
 * A escolha vai para localStorage num try/catch porque o acesso pode falhar
 * (janela privada, dado de site bloqueado) e o app tem de renderizar certo
 * mesmo assim.
 */

import { useEffect, useState } from 'react'

import { Icon, type IconName } from './Icon'

type Tema = 'sistema' | 'claro' | 'escuro' | 'ligado'

const CHAVE = 'pauta:tema'

const OPCOES: { valor: Tema; rotulo: string; icone: IconName | 'led' }[] = [
  { valor: 'claro', rotulo: 'Claro', icone: 'claro' },
  { valor: 'escuro', rotulo: 'Escuro', icone: 'escuro' },
  { valor: 'sistema', rotulo: 'Sistema', icone: 'sistema' },
  { valor: 'ligado', rotulo: 'Ligado', icone: 'led' },
]

/**
 * **O atributo vai em inglês, e o estado em português.** O CSS escreve
 * `[data-theme='dark']` e `[data-theme='light']`, que são as palavras do
 * padrão (`color-scheme`, `prefers-color-scheme`) e não uma escolha nossa.
 * Escrever `escuro` ali não casava com seletor nenhum: o botão ficava
 * marcado, o atributo mudava, e a tela não mudava de cor — defeito mudo,
 * porque nada quebra quando um seletor não casa. `ligado` é nome próprio, e
 * fica como está.
 */
const ATRIBUTO: Record<Exclude<Tema, 'sistema'>, string> = {
  claro: 'light',
  escuro: 'dark',
  ligado: 'ligado',
}

function ler(): Tema {
  try {
    const salvo = localStorage.getItem(CHAVE)
    if (salvo === 'claro' || salvo === 'escuro' || salvo === 'sistema' || salvo === 'ligado') return salvo
  } catch {
    // Sem acesso a localStorage: segue no padrão.
  }
  return 'sistema'
}

export function ThemeToggle() {
  const [tema, setTema] = useState<Tema>(ler)

  useEffect(() => {
    const raiz = document.documentElement
    if (tema === 'sistema') raiz.removeAttribute('data-theme')
    else raiz.setAttribute('data-theme', ATRIBUTO[tema])
    try {
      localStorage.setItem(CHAVE, tema)
    } catch {
      // Preferência não persiste; a sessão atual continua correta.
    }
  }, [tema])

  return (
    <div className="segmented" role="group" aria-label="Tema">
      {OPCOES.map((opcao) => (
        <button
          key={opcao.valor}
          type="button"
          className="segmented__option"
          aria-pressed={tema === opcao.valor}
          title={opcao.rotulo}
          onClick={() => setTema(opcao.valor)}
        >
          {opcao.icone === 'led' ? <span className="tema__led" aria-hidden /> : <Icon name={opcao.icone} size={14} />}
          <span className="sr-only">{opcao.rotulo}</span>
        </button>
      ))}
    </div>
  )
}
